import { extendTheme, type ThemeConfig } from "@chakra-ui/react";

export type Branding = {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoLightUrl: string;
  logoDarkUrl: string;
  faviconUrl: string;
};

export type Company = {
  name: string;
  tagline: string;
  address: string;
  phone: string;
  email: string;
  website: string;
};

export type PublicSettings = {
  company: Company;
  branding: Branding;
};

export const DEFAULT_BRANDING: Branding = {
  primaryColor: "#111827",
  secondaryColor: "#6B7280",
  accentColor: "#2563EB",
  logoLightUrl: "",
  logoDarkUrl: "",
  faviconUrl: "",
};

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

export function hexToRgb(hex: string) {
  const h = String(hex || "")
    .replace("#", "")
    .trim();

  if (![3, 6].includes(h.length)) return null;

  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;

  const num = parseInt(full, 16);
  if (!Number.isFinite(num)) return null;

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

export function rgbToHex(r: number, g: number, b: number) {
  const v = (n: number) =>
    clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${v(r)}${v(g)}${v(b)}`;
}

export function isHexLight(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const lum = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return lum > 0.65;
}

function mixHex(hexA: string, hexB: string, weight = 0.5) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return hexA;

  return rgbToHex(
    a.r + (b.r - a.r) * weight,
    a.g + (b.g - a.g) * weight,
    a.b + (b.b - a.b) * weight,
  );
}

function buildColorScale(baseHex: string) {
  return {
    50: mixHex(baseHex, "#ffffff", 0.92),
    100: mixHex(baseHex, "#ffffff", 0.84),
    200: mixHex(baseHex, "#ffffff", 0.68),
    300: mixHex(baseHex, "#ffffff", 0.5),
    400: mixHex(baseHex, "#ffffff", 0.24),
    500: baseHex,
    600: mixHex(baseHex, "#000000", 0.14),
    700: mixHex(baseHex, "#000000", 0.28),
    800: mixHex(baseHex, "#000000", 0.42),
    900: mixHex(baseHex, "#000000", 0.58),
  };
}

export function applyBrandingToDom(branding?: Partial<Branding>) {
  if (typeof document === "undefined") return;

  const next = { ...DEFAULT_BRANDING, ...(branding || {}) };
  const root = document.documentElement;

  root.style.setProperty("--brand-primary", next.primaryColor);
  root.style.setProperty("--brand-secondary", next.secondaryColor);
  root.style.setProperty("--brand-accent", next.accentColor);

  if (next.faviconUrl) {
    let el = document.querySelector(
      "link[rel='icon']",
    ) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement("link");
      el.rel = "icon";
      document.head.appendChild(el);
    }
    el.href = next.faviconUrl;
  }
}

export function buildChakraBrandTheme(branding?: Partial<Branding>) {
  const next = { ...DEFAULT_BRANDING, ...(branding || {}) };

  const accent = buildColorScale(next.accentColor);
  const primary = buildColorScale(next.primaryColor);
  const secondary = buildColorScale(next.secondaryColor);

  const accentForeground = isHexLight(next.accentColor) ? "gray.900" : "white";

  return extendTheme({
    config,
    colors: {
      brand: accent,
      brandPrimary: primary,
      brandSecondary: secondary,
    },
    fonts: {
      heading: "Inter, system-ui, sans-serif",
      body: "Inter, system-ui, sans-serif",
    },
    styles: {
      global: {
        body: {
          bg: "gray.50",
          color: next.primaryColor,
        },
        "::selection": {
          bg: accent[200],
        },
      },
    },
    components: {
      Button: {
        baseStyle: {
          borderRadius: "xl",
          fontWeight: "600",
        },
        defaultProps: {
          colorScheme: "brand",
        },
      },
      Badge: {
        baseStyle: {
          borderRadius: "full",
          textTransform: "none",
          fontWeight: "600",
        },
      },
      Input: {
        defaultProps: {
          focusBorderColor: "brand.500",
        },
      },
      Textarea: {
        defaultProps: {
          focusBorderColor: "brand.500",
        },
      },
      Select: {
        defaultProps: {
          focusBorderColor: "brand.500",
        },
      },
      Tabs: {
        variants: {
          softRounded: {
            tab: {
              borderRadius: "full",
              fontWeight: "600",
              _selected: {
                bg: "brand.500",
                color: accentForeground,
              },
            },
          },
        },
      },
      Card: {
        baseStyle: {
          container: {
            borderRadius: "2xl",
            borderWidth: "1px",
            borderColor: "gray.200",
            bg: "white",
            boxShadow: "sm",
          },
        },
      },
    },
  });
}
